/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon-toolkit',
	'prop-types'
], function(
	React,
	MobxReact,
	Toolkit,
	PropTypes
) {

	const { FlexLayout, HSpacer } = Toolkit;

	/**
	 * @class Split
	 * @description Split is a property layout control class of type container. It contains two children properties 'left' and 'right'/'top'
	 * and will render these 2 properties symetrically on the same row.  
	 * @example Example props usage to render a Split item:
	 * {
	 *		item: // see example of Split item
	 *		className: 'classNameForThisSplit',
	 *		makeSectionItemCb: f() // internal callback function to generate JSX for each property child
	 *		logger: object // to log warnings if spec invalid
	 * }
	 * 
	 *  Example of Split item:
	 * {
	 *	'id': 'moveWidgetSplit',
	 *	'type': 'Split',
	 *	'items': [{
	 *		'id': 'left',
	 *		'align': 'left',
	 *		'items': [{
	 *			'type': 'InputLabel',
	 *			'label': 'X',
	 *			'multiline': true,
	 *			'handleReturnKey': true,
	 *			'inputType': 'number',
	 *			'decimalPlaces': 2,
	 *			'style': 'width: 140px',
	 *			'id': 'left',
	 *			'readOnly': false
	 *		}]
	 *	}, {
	 *		'id': 'right',
	 *		'align': 'right',
	 *		'items': [{
	 *			'type': 'InputLabel',
	 *			'label': 'Y',
	 *			'multiline': true,
	 *			'handleReturnKey': true,
	 *			'inputType': 'number',
	 *			'decimalPlaces': 2,
	 *			'style': 'width: 140px',
	 *			'id': 'top',
	 *			'readOnly': false
	 *		}]
	 *	}]
	 * }
	 */
	class Split extends React.Component {

		render() {
			const { item, className, logger } = this.props;
			const splitItems = this._getSplitItems(item);
			if (!splitItems.length) {
				logger.warn(`split item '${item.id}' does not have 2 children: left and right`);
				return null;
			}

			return (
				<FlexLayout
					className={className}
					direction='row'
					wrap='nowrap'
					justifyContent='space-between'
				>
					{splitItems}
				</FlexLayout>
			);
		}

		_getSplitItems(layoutItem) {
			const splitItems = [];
			if (layoutItem && layoutItem.items && layoutItem.items.length === 2) {
				const left = layoutItem.items.find(splitItem => splitItem.id === 'left');
				const right = layoutItem.items.find(splitItem => splitItem.id === 'top' || splitItem.id === 'right');

				if (left && right) {
					splitItems.push(this.props.makeSectionItemCb(left.items[0], 0));
					splitItems.push(<HSpacer size={1}/>);
					splitItems.push(this.props.makeSectionItemCb(right.items[0], 1));
				}
			}
			return splitItems;
		}
	}

	Split.propTypes = {
		item: PropTypes.shape({
			id: PropTypes.string.isRequired,
			type: 'Split',
			items: PropTypes.array.isRequired
		}),
		className: PropTypes.string,
		makeSectionItemCb: PropTypes.func.isRequired,
		logger: PropTypes.object.isRequired
	};

	return MobxReact.observer(Split);
});